import { useState, FormEvent, useEffect } from 'react';
import { Root, Field, Message, Label, Control } from '@radix-ui/react-form';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import tw from 'twin.macro';

import { LoginType } from '@/types/apiTypes';
import { Button, Card, Link, Input, Icon, LoadingOverlay} from '@/components';
import { useAuth } from '@/context/AuthContext';
import strings from '@/language/Swedish';
import { PATH } from '@/routes/Paths';
import Logo from '@/assets/images/valueguard-logo.webp';
import BackgroundImage from '../../assets/images/3Dcity-1.webp';
import { ButtonColor, ButtonSize, Icons, IconSize, IconColor } from '@/enums';
import ForgotPassword from './ForgotPassword';
import useLogin from '@/api/mutations/useLogin';
const StyledLogin = styled.div`
  ${tw`w-full min-h-dvh flex items-center justify-center p-4`};
`;

const Background = styled.div`
  ${tw`absolute inset-0 md:inset-3 md:rounded-3xl bg-cover bg-center`};
`;

const Image = styled.img`
  ${tw`w-44 h-auto mx-auto block mb-8`};
`;

const LoginText = styled.p`
  ${tw`text-slate-600 mb-8 text-center`};
`;

const StyledRoot = styled(Root)`
  ${tw`grid mb-6`};
`;

const LabelContainer = styled.div`
  ${tw`flex items-baseline justify-between`};
`;

const StyledLabel = styled(Label)`
  ${tw`mb-1 text-slate-600 has-[:focus]:text-primary`};
`;

const StyledField = styled(Field)`
  ${tw`mb-6`};
`;

const StyledInput = styled(Input)`
  ${tw`box-border w-full bg-white/70 inline-flex h-10 appearance-none items-center justify-center rounded-lg px-3 leading-none text-slate-800 border border-slate-300 outline-none transition`};

  &:hover {
    ${tw`border-slate-600`};
  }

  &:focus {
    ${tw`bg-white border-primary`};
  }

  &:active {
    ${tw`bg-white border-primary shadow-customFocus`};
  }
`;

const StyledControl = styled(Control)`
  ${tw`flex flex-col`};
`;

const StyledMessage = styled(Message)`
  ${tw`text-xs text-red-500`};
`;

const BottomContainer = styled.div`
  ${tw`flex justify-center mt-4`};
`;

const StyledErrorMsg = styled.div`
${tw`text-sm mb-5 bg-white rounded-lg border-2 p-2 flex items-center flex-col gap-2 text-left`};
`;

const loginObject: LoginType = {
  client_id: 'api',
  grant_type: 'password',
  username: '',
  password: '',
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isResetPassword,setIsResetPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const { isError, isPending, mutate: loginMutation } = useLogin();

  useEffect(() => {
    setIsResetPassword(false);
  },[isError]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    loginMutation(
      { ...loginObject, username, password },
      {
        onSuccess: (response) => {
          if (response !== null) {
            const { access_token, refresh_token, expires_in } = response;
            login(access_token, refresh_token, expires_in);
            navigate(PATH.home);
          }
        },
      }
    );
  };

  const handleForgotPasswordClick = () => {};
  return (
    <StyledLogin>
      <Background style={{ backgroundImage: `url(${BackgroundImage})` }} />
      <Card>
        <Image src={Logo} alt={strings.login.logo} />
        <LoginText>{strings.login.loginText}</LoginText>
        {isPending && <LoadingOverlay borderRadius="rounded-3xl" />}
        {isError && !isResetPassword && <StyledErrorMsg><Icon icon={Icons.important} size={IconSize.xlarge} color={IconColor.warning} />{strings.data.somethingWentWrongLogin}</StyledErrorMsg>}
        {isResetPassword && <StyledErrorMsg><Icon icon={Icons.important} size={IconSize.xlarge} color={IconColor.success} />{strings.data.resetPasswordMessage}</StyledErrorMsg>}
        <StyledRoot onSubmit={handleSubmit}>
          <StyledField name="E-post">
            <LabelContainer>
              <StyledLabel htmlFor="email">{strings.login.email}</StyledLabel>
            </LabelContainer>
            <StyledControl asChild>
              <StyledInput
                type="email | text"
                autoComplete="off"
                id="email"
                name="email"
                required
                onChange={(e) => setUsername(e.target.value)}
              />
            </StyledControl>
            <StyledMessage match="valueMissing">
              {strings.login.emailMessage1}
            </StyledMessage>
            <StyledMessage match="typeMismatch">
              {strings.login.emailMessage2}
            </StyledMessage>
          </StyledField>
          <StyledField name="password">
            <LabelContainer>
              <StyledLabel htmlFor="password">
                {strings.login.password}
              </StyledLabel>
              <ForgotPassword setIsResetPassword={setIsResetPassword}>
                <Button
                  onClick={() => handleForgotPasswordClick()}
                  type="button">
                  {strings.login.passwordForgot}
                </Button>
              </ForgotPassword>
            </LabelContainer>
            <StyledControl asChild>
              <StyledInput
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                id="password"
                name="password"
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </StyledControl>
            <Button
              type="button"
              className="float-right"
              onClick={() => setShowPassword(!showPassword)}>
              {showPassword
                ? strings.login.togglePasswordOff
                : strings.login.togglePasswordOn}
            </Button>

            <StyledMessage match="valueMissing">
              {strings.login.passwordMessage}
            </StyledMessage>
          </StyledField>
          <Button
            type="submit"
            id="submit"
            color={ButtonColor.primary}
            size={ButtonSize.fullWidth}>
            {strings.login.login}
          </Button>
        </StyledRoot>
        <BottomContainer>
          <Link href={`mailto:${strings.login.emailUrl.email}`}>
            {strings.login.contactSupport}
          </Link>
        </BottomContainer>
      </Card>
    </StyledLogin>
  );
};

export default Login;
